/**
 * @file DeliveryAgreementPreview.tsx
 * @description Предпросмотр письма клиенту в блоке согласования доставки
 * (phase19.24 шаг 5.1, §3).
 *
 * Менеджер обязан видеть ровно то, что уйдёт клиенту: способ получения с
 * адресом и графиком, суммы и порядок оплаты. До предпросмотра о содержании
 * письма можно было судить только по факту отправки — и «Доставка 450 ₽» без
 * перевозчика уезжала клиенту как есть.
 *
 * Раскрытие — кнопка с `aria-expanded`, а не `<details>`: нативный
 * disclosure со снятым маркером (`list-none`) не читается как «сюда можно
 * нажать» — QA шага 5.1 не смог его раскрыть. Кнопка даёт видимую стрелку,
 * работает с клавиатуры и объявляется озвучкой.
 *
 * Свёрнут по умолчанию: это проверка перед отправкой, а не постоянная часть
 * экрана.
 */

import { ChevronDown, ChevronRight } from "lucide-react";
import { useId, useState } from "react";

import { formatMoney } from "../../lib/api-orders";
import { SHIPMENT_CARRIER_LABEL } from "../../lib/api-orders";
import type { OrderPaymentTerms } from "../../lib/api-orders";
import type { ShopPickupLocationDto } from "../../lib/api-shop-pickup";
import {
  isSelectableReceiptMethod,
  PAYMENT_TERMS_LABEL,
  RECEIPT_METHOD_META,
  receiptDeliveryAmount,
  type ReceiptFormValue,
} from "../../lib/receipt-method";

interface Props {
  value: ReceiptFormValue;
  paymentTerms: OrderPaymentTerms;
  /** Сумма товаров заказа в рублях (строкой из заявки). */
  itemsTotal: string;
  pickup: ShopPickupLocationDto | null;
}

export function DeliveryAgreementPreview({
  value,
  paymentTerms,
  itemsTotal,
  pickup,
}: Props) {
  const [open, setOpen] = useState(false);
  const bodyId = useId();
  const delivery = receiptDeliveryAmount(value);
  const total = (Number(itemsTotal) || 0) + (delivery ?? 0);
  const meta = RECEIPT_METHOD_META[value.method];
  const carrierName = value.carrier ? SHIPMENT_CARRIER_LABEL[value.carrier] : null;

  return (
    <div className="rounded-xl bg-surface-low px-4 py-2">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={bodyId}
        onClick={() => setOpen((v) => !v)}
        className="flex min-h-12 w-full items-center gap-1.5 rounded-md text-left text-sm font-medium text-on-surface hover:text-primary focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
      >
        {open ? (
          <ChevronDown className="size-4 shrink-0" aria-hidden />
        ) : (
          <ChevronRight className="size-4 shrink-0" aria-hidden />
        )}
        <span className="min-w-0 break-words">Предпросмотр письма</span>
      </button>
      {/* Содержимое остаётся в разметке (скрыто классом), чтобы `aria-controls`
          указывал на существующий узел и озвучка находила его сразу. */}
      <div
        id={bodyId}
        className={
          open ? "flex flex-col gap-1.5 pb-2 text-sm text-on-surface" : "hidden"
        }
      >
        <p>
          <span className="text-on-surface-variant">Способ получения: </span>
          {value.method === "PICKUP"
            ? `Самовывоз${pickup?.address ? `, ${pickup.address}` : " — адрес пункта уточняется"}`
            : value.method === "CARRIER"
              ? `${carrierName ?? "перевозчик не выбран"}${value.address ? `, ${value.address}` : ""}`
              : value.method === "SHOP_COURIER"
                ? `Курьер магазина${value.address ? `, ${value.address}` : ""}`
                : "Способ ещё не выставлен — выберите, как клиент получит заказ"}
        </p>
        {value.method === "PICKUP" ? (
          <p className="text-on-surface-variant">
            Время работы: {pickup?.workingHours || "уточнит менеджер"}
            {pickup?.phone ? ` · Телефон: ${pickup.phone}` : ""}
          </p>
        ) : null}
        <p>
          Товары {formatMoney(itemsTotal)} ₽ · Доставка{" "}
          {delivery === null
            ? meta.deliveryNote
            : delivery === 0
              ? "0 ₽ (бесплатно)"
              : `${formatMoney(String(delivery))} ₽`}{" "}
          · ИТОГО {formatMoney(String(total))} ₽ ·{" "}
          {PAYMENT_TERMS_LABEL[paymentTerms].toLowerCase()}
        </p>
        {value.comment.trim() ? (
          <p>
            <span className="text-on-surface-variant">Комментарий: </span>
            {value.comment.trim()}
          </p>
        ) : null}
        <p className="text-on-surface-variant">
          {/* Способ из истории (`AGREEMENT`): отправлять нечего, пока менеджер
              не выбрал один из трёх исходов. */}
          {isSelectableReceiptMethod(value.method)
            ? "Кнопка в письме: «Подтверждаю» · ссылка действует 72 часа."
            : "Письмо не отправить: сначала выберите способ получения."}
        </p>
      </div>
    </div>
  );
}
